import * as React from 'react';
import { FilteringStyledOptions, StyledOptions } from '@emotion/styled/types/base';
import { PropsOf, ThemeProvider as BaseThemeProvider, useTheme as useBaseTheme } from '@emotion/react';
import _styled from '@emotion/styled';
import fromParis from 'lodash-es/fromPairs';

import type { Interpolation, CreateStyledComponent } from '@emotion/styled';

export const media = {
  sm: '@media (min-width: 25em)',
  md: '@media (min-width: 40em)',
  lg: '@media (min-width: 75em)',
} as const;

export type MediaType = keyof typeof media;
export type MediaSelector = (typeof media)[MediaType];

export type ObjectInterpolation<T> = Extract<Interpolation<T>, Record<string, unknown>>;

export type CustomTheme = {
  typography: {
    eventHeader: ObjectInterpolation<any>;
    cartoonStyleText: ObjectInterpolation<any>;
  };
  navigationHeight: string;
  media: typeof media;
  onMedia: typeof onMedia;
  reducedMotion: typeof reducedMotion;
};

declare module '@emotion/react' {
  export interface Theme extends CustomTheme {}
}

export interface CreateStyled<Theme> {
  <
    C extends React.ComponentType<React.ComponentProps<C>>,
    ForwardedProps extends Extract<keyof React.ComponentProps<C>, string> = Extract<
      keyof React.ComponentProps<C>,
      string
    >,
  >(
    component: C,
    options: FilteringStyledOptions<React.ComponentProps<C>, ForwardedProps>,
  ): CreateStyledComponent<
    Pick<PropsOf<C>, ForwardedProps> & {
      theme?: Theme;
      as?: React.ElementType;
    }
  >;

  <C extends React.ComponentType<React.ComponentProps<C>>>(
    component: C,
    options?: StyledOptions<React.ComponentProps<C>>,
  ): CreateStyledComponent<
    PropsOf<C> & {
      theme?: Theme;
      as?: React.ElementType;
    }
  >;

  <
    Tag extends keyof JSX.IntrinsicElements,
    ForwardedProps extends Extract<keyof JSX.IntrinsicElements[Tag], string> = Extract<
      keyof JSX.IntrinsicElements[Tag],
      string
    >,
  >(
    tag: Tag,
    options: FilteringStyledOptions<JSX.IntrinsicElements[Tag], ForwardedProps>,
  ): CreateStyledComponent<{ theme?: Theme; as?: React.ElementType }, Pick<JSX.IntrinsicElements[Tag], ForwardedProps>>;

  <Tag extends keyof JSX.IntrinsicElements>(
    tag: Tag,
    options?: StyledOptions<JSX.IntrinsicElements[Tag]>,
  ): CreateStyledComponent<{ theme?: Theme; as?: React.ElementType }, JSX.IntrinsicElements[Tag]>;
}

export type StyledTags<Theme> = {
  [Tag in keyof JSX.IntrinsicElements]: CreateStyledComponent<
    {
      theme?: Theme;
      as?: React.ElementType;
    },
    JSX.IntrinsicElements[Tag]
  >;
};

export function onMedia<MP = unknown>(
  media: MediaType | MediaType[],
  style: ObjectInterpolation<MP>,
): {
  [key in MediaSelector]?: ObjectInterpolation<MP>;
} {
  if (Array.isArray(media)) {
    return fromParis(media.map((m) => [m, style]));
  }
  return { [media]: style };
}

export const reducedMotion = '@media (prefers-reduced-motion)' as const;

export function makeTheme<BaseTheme extends Record<string, unknown>, Theme extends Record<string, unknown>>(
  defaultTheme?: Theme,
) {
  const ThemeProvider: React.FC<
    React.PropsWithChildren<{
      theme: Partial<BaseTheme> & Theme;
    }>
  > = ({ theme, children }) => {
    const baseTheme = useBaseTheme();
    const mergedTheme = React.useMemo(() => ({ ...defaultTheme, ...baseTheme, ...theme }), [baseTheme, theme]);
    return <BaseThemeProvider theme={mergedTheme}>{children}</BaseThemeProvider>;
  };
  const styled = _styled;
  const useTheme = () => useBaseTheme();

  return { styled, ThemeProvider, useTheme };
}
